import React, { useEffect, useRef, useState } from "react";
import {
  Box, Button, CircularProgress, Grid, TextField, Typography,
  Divider, IconButton, Snackbar, Alert, Chip, Avatar,
} from "@mui/material";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { Colors } from "../../Theme/Theme";
import { FormOption } from "../../api/requests/FormOptions";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";

const CreateFormOptions = () => {
  const [formData, setFormData] = useState({
    form_type: "hospital_cash",
    form_field: "hospital_cash",
    options: { values: [{ value: "", label: "" }] },
  });
  const [formOptions, setFormOptions] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const fetchedFields = useRef(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleOptionChange = (index, key, value) => {
    const updatedOptions = [...formData.options.values];
    updatedOptions[index][key] = value;
    setFormData({ ...formData, options: { values: updatedOptions } });
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: { values: [...formData.options.values, { value: "", label: "" }] },
    });
  };

  const removeOption = (index) => {
    if (formData.options.values.length <= 1) return;
    const updatedOptions = formData.options.values.filter((_, i) => i !== index);
    setFormData({ ...formData, options: { values: updatedOptions } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        form_type: formData.form_type,
        form_field: formData.form_field,
        options: { values: formData.options.values.filter((o) => o.value && o.label) },
      };
      if (formData.id) {
        await FormOption.updateFormOptions(formData.id, payload);
        setFormOptions((prevList) =>
          prevList.map((form) =>
            form.id === formData.id ? { ...form, ...payload } : form
          )
        );
        setSnack({ open: true, message: "Options updated successfully", severity: "success" });
      } else {
        const response = await FormOption.createFormOptions(payload);
        setSnack({ open: true, message: response?.message || "Options created successfully", severity: "success" });
        fetchedFields.current.clear();
        setFormOptions([]);
        const fields = ["hospital_cash", "business_loan", "home_loan", "personal_loan", "ca_registration"];
        fields.forEach((field) => getFormOptionsFields(field));
      }
    } catch (error) {
      setSnack({ open: true, message: "Error saving options. Please try again.", severity: "error" });
    } finally {
      setSaving(false);
      setIsEdit(false);
      setFormData({
        form_type: "hospital_cash",
        form_field: "hospital_cash",
        options: { values: [{ value: "", label: "" }] },
      });
    }
  };

  const getFormOptionsFields = async (fieldName) => {
    setLoading(true);
    if (fetchedFields.current.has(fieldName)) return;
    fetchedFields.current.add(fieldName);
    try {
      const { data } = await FormOption.getFormOptions(fieldName);
      if (data?.length > 0) {
        const newOptions = data.reduce((acc, item) => {
          const formType = item.form_type;
          const fieldData = {
            id: item.id,
            form_field: item.form_field.trim(),
            options: { values: item.options?.values || [] },
          };
          const existingIndex = acc.findIndex((group) => group.form_type === formType);
          if (existingIndex !== -1) {
            acc[existingIndex].fields.push(fieldData);
          } else {
            acc.push({ form_type: formType, fields: [fieldData] });
          }
          return acc;
        }, []);
        setFormOptions((prevOptions) => [...prevOptions, ...newOptions]);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fields = ["hospital_cash", "business_loan", "home_loan", "personal_loan", "ca_registration"];
    fields.forEach((field) => getFormOptionsFields(field));
  }, []);

  const handleFieldEdit = (groupIndex, fieldIndex) => {
    const selectedForm = formOptions[groupIndex].fields[fieldIndex];
    const formType = formOptions[groupIndex].form_type;
    setIsEdit(true);
    setFormData({
      id: selectedForm?.id,
      form_type: formType,
      form_field: selectedForm?.form_field,
      options: { values: [...(selectedForm?.options.values || [])] },
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const hospitalGroup = formOptions.find((g) => g.form_type === "hospital_cash");
  const hospitalFields = hospitalGroup?.fields || [];
  const hospitalOptions = hospitalFields.reduce((acc, f) => {
    return [...acc, ...(f.options?.values || [])];
  }, []);

  const otherGroups = formOptions.filter((g) => g.form_type !== "hospital_cash");

  return (
    <Box sx={{
      width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` },
      minHeight: "100vh", backgroundColor: Colors.bgColor,
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <LocalHospitalIcon sx={{ fontSize: 22, color: Colors.insuranceGreen || "#059669" }} />
            <Typography sx={{ fontWeight: 500, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              Manage Form Options
            </Typography>
          </Box>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "13px", fontWeight: 400 }}>
            Add or edit hospitals and other dropdown options that appear in user forms like HospiCash.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Hospital Quick View */}
          <Grid item xs={12} md={5}>
            <Box sx={{
              backgroundColor: Colors.white, borderRadius: "12px",
              border: `1px solid ${Colors.dividerColor}`, p: 2.5,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Avatar sx={{ width: 32, height: 32, backgroundColor: "#ECFDF5", color: "#059669" }}>
                  <LocalHospitalIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: "14px", fontWeight: 500, color: Colors.primaryTextColor }}>
                    Current Hospitals
                  </Typography>
                  <Typography sx={{ fontSize: "11px", fontWeight: 400, color: Colors.secondaryColor }}>
                    These appear in the HospiCash form dropdown
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ borderColor: Colors.dividerColor, mb: 2 }} />

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} sx={{ color: "#059669" }} />
                </Box>
              ) : hospitalOptions.length === 0 ? (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <LocalHospitalIcon sx={{ fontSize: 32, color: Colors.dividerColor, mb: 1 }} />
                  <Typography sx={{ fontSize: "13px", fontWeight: 400, color: Colors.secondaryColor }}>
                    No hospitals added yet. Use the form to add hospitals.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {hospitalOptions.map((opt, i) => (
                    <Chip
                      key={i}
                      label={opt.label}
                      size="small"
                      sx={{
                        backgroundColor: "#ECFDF5", color: "#059669",
                        fontSize: "12px", fontWeight: 400,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  ))}
                </Box>
              )}

              {hospitalFields.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    size="small" startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                    onClick={() => handleFieldEdit(formOptions.indexOf(hospitalGroup), 0)}
                    sx={{
                      fontSize: "12px", fontWeight: 500, color: "#059669",
                      textTransform: "none", "&:hover": { backgroundColor: "#ECFDF5" },
                    }}
                  >
                    Edit Hospital List
                  </Button>
                </Box>
              )}
            </Box>

            {/* Other Form Options */}
            {otherGroups.length > 0 && (
              <Box sx={{
                backgroundColor: Colors.white, borderRadius: "12px",
                border: `1px solid ${Colors.dividerColor}`, p: 2.5, mt: 2,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <Avatar sx={{ width: 32, height: 32, backgroundColor: Colors.blueSoft, color: Colors.primary }}>
                    <SettingsIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Typography sx={{ fontSize: "14px", fontWeight: 500, color: Colors.primaryTextColor }}>
                    Other Form Options
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: Colors.dividerColor, mb: 2 }} />
                {otherGroups.map((item, groupIndex) => {
                  const realIdx = formOptions.indexOf(item);
                  return (
                    <Box key={groupIndex} sx={{ mb: 2 }}>
                      <Typography sx={{ fontSize: "12px", fontWeight: 500, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px", mb: 1 }}>
                        {item.form_type.replace(/_/g, " ")}
                      </Typography>
                      {item.fields?.map((field, fieldIndex) => (
                        <Box key={field.id} sx={{
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          p: 1.5, border: `1px solid ${Colors.dividerColor}`, borderRadius: "8px", mb: 1,
                        }}>
                          <Box>
                            <Typography sx={{ fontSize: "13px", fontWeight: 400, color: Colors.primaryTextColor }}>
                              {field.form_field.replace(/_/g, " ")}
                            </Typography>
                            <Typography sx={{ fontSize: "11px", fontWeight: 400, color: Colors.secondaryColor }}>
                              {field.options?.values?.length || 0} options
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={() => handleFieldEdit(realIdx, fieldIndex)} sx={{ color: Colors.primary }}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Grid>

          {/* Form */}
          <Grid item xs={12} md={7}>
            <Box component="form" onSubmit={handleSubmit} sx={{
              backgroundColor: Colors.white, borderRadius: "12px",
              border: `1px solid ${Colors.dividerColor}`, p: 2.5,
            }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 500, color: Colors.primaryTextColor, mb: 0.5 }}>
                {isEdit ? "Edit Options" : "Add New Options"}
              </Typography>
              <Typography sx={{ fontSize: "12px", fontWeight: 400, color: Colors.secondaryColor, mb: 2 }}>
                {isEdit
                  ? "Modify the values below and save."
                  : "To add hospitals for HospiCash, keep Form Type and Form Field as \"hospital_cash\". Add each hospital name."}
              </Typography>
              <Divider sx={{ borderColor: Colors.dividerColor, mb: 2.5 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 500, color: Colors.primaryTextColor, mb: 0.5 }}>Form Type</Typography>
                  <TextField
                    size="small" name="form_type" value={formData.form_type}
                    fullWidth required onChange={handleChange} disabled={isEdit}
                    placeholder="e.g. hospital_cash"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px", fontSize: "13px",
                        "& fieldset": { borderColor: Colors.dividerColor },
                        "&:hover fieldset": { borderColor: Colors.primary },
                        "&.Mui-focused fieldset": { borderColor: Colors.primary },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 500, color: Colors.primaryTextColor, mb: 0.5 }}>Form Field</Typography>
                  <TextField
                    size="small" name="form_field" value={formData.form_field}
                    fullWidth required onChange={handleChange} disabled={isEdit}
                    placeholder="e.g. hospital_cash"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px", fontSize: "13px",
                        "& fieldset": { borderColor: Colors.dividerColor },
                        "&:hover fieldset": { borderColor: Colors.primary },
                        "&.Mui-focused fieldset": { borderColor: Colors.primary },
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2.5, mb: 1 }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: Colors.primaryTextColor }}>
                  Options (Hospital Names)
                </Typography>
              </Box>

              {formData.options.values.map((option, index) => (
                <Box key={index} sx={{ display: "flex", gap: 1.5, mb: 1.5, alignItems: "center" }}>
                  <Box sx={{
                    width: 24, height: 24, borderRadius: "6px", backgroundColor: "#ECFDF5",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#059669" }}>{index + 1}</Typography>
                  </Box>
                  <TextField
                    size="small" fullWidth value={option.value}
                    placeholder="Value (e.g. apollo_hospitals)"
                    onChange={(e) => handleOptionChange(index, "value", e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px", fontSize: "13px",
                        "& fieldset": { borderColor: Colors.dividerColor },
                        "&:hover fieldset": { borderColor: Colors.primary },
                        "&.Mui-focused fieldset": { borderColor: Colors.primary },
                      },
                    }}
                  />
                  <TextField
                    size="small" fullWidth value={option.label}
                    placeholder="Label (e.g. Apollo Hospitals)"
                    onChange={(e) => handleOptionChange(index, "label", e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px", fontSize: "13px",
                        "& fieldset": { borderColor: Colors.dividerColor },
                        "&:hover fieldset": { borderColor: Colors.primary },
                        "&.Mui-focused fieldset": { borderColor: Colors.primary },
                      },
                    }}
                  />
                  <IconButton
                    onClick={() => removeOption(index)} size="small"
                    disabled={formData.options.values.length <= 1}
                    sx={{ color: "#EF4444", "&:disabled": { color: Colors.dividerColor } }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              ))}

              <Button
                onClick={addOption} startIcon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
                sx={{
                  mt: 0.5, fontSize: "12px", fontWeight: 500, color: Colors.primary,
                  textTransform: "none", "&:hover": { backgroundColor: Colors.blueSoft },
                }}
              >
                Add Another Option
              </Button>

              <Divider sx={{ borderColor: Colors.dividerColor, my: 2 }} />

              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button
                  type="submit" variant="contained" disabled={saving}
                  startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    backgroundColor: "#059669", color: "#fff", fontWeight: 500,
                    py: 1, px: 3, borderRadius: "8px", fontSize: "13px",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#047857" },
                  }}
                >
                  {formData.id ? "Update Options" : "Save Options"}
                </Button>
                {isEdit && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setIsEdit(false);
                      setFormData({
                        form_type: "hospital_cash", form_field: "hospital_cash",
                        options: { values: [{ value: "", label: "" }] },
                      });
                    }}
                    sx={{
                      borderColor: Colors.dividerColor, color: Colors.secondaryColor,
                      fontWeight: 500, py: 1, px: 3, borderRadius: "8px", fontSize: "13px",
                      textTransform: "none",
                      "&:hover": { borderColor: Colors.primary, color: Colors.primary },
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} sx={{ borderRadius: "8px" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateFormOptions;
